import { useFormik } from 'formik'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import * as Yup from 'yup'
import {
  AtendimentoType,
  ButtonType,
  ModalidadeType,
  OptionsHelper,
  QUERIES,
  showAlert,
  showError,
} from '../../../../../_metronic/helpers'
import {
  Button,
  FetchState,
  FormDateInput,
  FormInput,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../components'
import { getEmpresaById, getModalidadesByEmpresa } from '../../empresa/empresas-list/core/_requests'
import { extrairErrosValidacao } from '../../escalas/core/escalaHelpers'
import { PacienteView } from '../../paciente-management/pacientes-list/core/_models'
import { getUnidades } from '../../unidades/unidades-list/core/_requests'
import { AgendamentoDtoCreate, HorarioDisponivel } from '../core/_models'
import { criaAgendamento, getDisponibilidade } from '../core/_requests'
import {
  MENSAGEM_CONFLITO_PADRAO,
  MENSAGEM_PROXIMO_PASSO_CONFLITO,
  ROTULO_VAGA_POOL,
  dataDoInicio,
  descreveFalhaAgenda,
  descreveOcupacao,
  ehConflitoDeAgenda,
  formataDiaPorExtenso,
  hojeIso,
  horaDoInicio,
  rotuloProfissional,
  somaDias,
} from '../core/agendaHelpers'
import { AgendaGradeHorarios } from './AgendaGradeHorarios'
import { AvisoAgenda } from './AvisoAgenda'

/**
 * Valor da opção "Equipe" do seletor de profissional. **Não** é ausência de
 * escolha: pool é uma vaga com regra própria (fechadura diferente da nominal), e
 * "vazio significa equipe" seria invisível para quem usa a tela.
 */
const VALOR_POOL = 'pool'

/** Janela padrão da consulta: hoje + 6 dias. */
const DIAS_PADRAO = 6

/** Teto da janela, para não pedir uma grade gigante de uma vez. */
const MAX_DIAS_INTERVALO = 31

/** Tipos de atendimento que não têm especialidade (mesmo recorte do `NewAtendimentoModal`). */
const TIPOS_SEM_ESPECIALIDADE: AtendimentoType[] = [
  AtendimentoType.Ocupacional,
  AtendimentoType.Screening,
  AtendimentoType.Telekit,
]

const exigeEspecialidade = (tipo?: IOption<AtendimentoType> | null): boolean =>
  !!tipo && !TIPOS_SEM_ESPECIALIDADE.includes(tipo.value)

type ValoresFormulario = {
  unidade: IOption<number> | null
  tipoAtendimento: IOption<AtendimentoType> | null
  especialidade: IOption<number> | null
  modalidade: IOption<ModalidadeType> | null
  profissional: IOption<string | number> | null
  inicio: string
  fim: string
  observacao: string
}

const schema = Yup.object({
  tipoAtendimento: Yup.object().nullable().required('Selecione o tipo de atendimento'),
  modalidade: Yup.object().nullable().required('Selecione a modalidade'),
  especialidade: Yup.object()
    .nullable()
    .when('tipoAtendimento', {
      is: exigeEspecialidade,
      then: (s) => s.required('Selecione a especialidade'),
      otherwise: (s) => s,
    }),
  inicio: Yup.string().required('Informe o início do período'),
  fim: Yup.string()
    .required('Informe o fim do período')
    .test('depois', 'O fim do período deve ser igual ou posterior ao início', function (valor) {
      return !valor || !this.parent.inicio || valor >= this.parent.inicio
    })
    .test('janela', `O período não pode passar de ${MAX_DIAS_INTERVALO} dias`, function (valor) {
      const inicio = this.parent.inicio
      if (!valor || !inicio) return true
      return valor <= somaDias(inicio, MAX_DIAS_INTERVALO - 1)
    }),
  observacao: Yup.string().max(255, 'A observação tem no máximo 255 caracteres'),
})

/**
 * Agendar um horário a partir da ficha do paciente.
 *
 * **Por que aqui e não numa tela de agenda:** a empresa da reserva é a do
 * paciente (`AgendamentoServiceImpl.reserva()` faz
 * `empresaService.findById(paciente.getEmpresa().getId())`) e o `POST /agendamentos`
 * é gateado por `hasPermission(#dto.paciente, 'Paciente', 'Agendamento:create')`.
 * Partindo da ficha, o escopo de tenant sai correto — é o mesmo motivo pelo qual
 * o `NewAtendimentoModal` carrega tudo por `paciente.empresa.id`, e este modal o
 * espelha.
 *
 * Quatro regras do backend que a tela respeita literalmente:
 *
 * 1. **`disponivel` é decisão do servidor** (ver `agendaHelpers.estadoDoHorario`).
 * 2. **O 409 é genérico de propósito** — o texto do servidor é exibido como veio.
 *    O índice `uk_agendamentos_pessoa_horario` atravessa tenants, então inferir
 *    ou mostrar quem ocupou vazaria ocupação de outra empresa.
 * 3. **Vaga esgotada é resposta de negócio**, não falha: mensagem clara e
 *    recarga da grade, sem toast de erro.
 * 4. **`POST /agendamentos/hold` não é usado** — ver `core/_requests.ts`.
 */
export const AgendarPacienteModal: FC = () => {
  const { closeModal, fetchStates } = useModal()
  const { data: paciente } = (fetchStates['paciente'] || {}) as FetchState<PacienteView>

  const [optionsTiposAtendimento, setOptionsTiposAtendimento] = useState<
    IOption<AtendimentoType>[]
  >([])
  const [optionsModalidades, setOptionsModalidades] = useState<IOption<ModalidadeType>[]>([])
  const [optionsUnidades, setOptionsUnidades] = useState<IOption<number>[]>([])
  const [carregandoOpcoes, setCarregandoOpcoes] = useState(true)

  const [horarioSelecionado, setHorarioSelecionado] = useState<HorarioDisponivel>()
  const [mensagemConflito, setMensagemConflito] = useState<string>()
  // Falha do POST de reserva (o 409 tem aviso próprio, `mensagemConflito`).
  const [falhaReserva, setFalhaReserva] = useState<string[]>([])
  const avisoConflitoRef = useRef<HTMLDivElement>(null)

  // Depois do 409 o horário escolhido some e o botão "Confirmar reserva" fica
  // desabilitado — o foco cairia no `body`. Levar o foco para o aviso mantém a
  // navegação por teclado no lugar onde a informação nova está.
  useEffect(() => {
    if (mensagemConflito) avisoConflitoRef.current?.focus()
  }, [mensagemConflito])

  const empresaId = paciente?.empresa?.id

  // Todas as opções saem da EMPRESA DO PACIENTE, não da empresa de quem está
  // logado — é a empresa que o backend usa para montar a vaga.
  useEffect(() => {
    if (!empresaId) return

    const carregar = async () => {
      setCarregandoOpcoes(true)
      try {
        const empresa = await getEmpresaById(empresaId)
        const unidades = await getUnidades(`page=0&size=200&search=&idEmpresa=${empresa.id}`)
        const modalidades = await getModalidadesByEmpresa(Number(empresaId))

        setOptionsUnidades(OptionsHelper.createOptions(unidades.data || [], 'nome', 'id'))
        setOptionsTiposAtendimento(
          OptionsHelper.createOptions(
            (empresa?.tiposAtendimentoMap || []).filter(
              (tipo) => tipo.name !== AtendimentoType.Interconsulta
            ),
            'descricao',
            'name'
          )
        )
        setOptionsModalidades(
          OptionsHelper.createOptions(
            (modalidades || []).filter((modalidade) => modalidade.name !== ModalidadeType.Parecer),
            'descricao',
            'name'
          )
        )
      } catch (error) {
        showError(error)
      } finally {
        setCarregandoOpcoes(false)
      }
    }

    carregar()
  }, [empresaId])

  const formik = useFormik<ValoresFormulario>({
    validationSchema: schema,
    initialValues: {
      unidade: null,
      tipoAtendimento: null,
      especialidade: null,
      modalidade: null,
      profissional: null,
      inicio: hojeIso(),
      fim: somaDias(hojeIso(), DIAS_PADRAO),
      observacao: '',
    },
    onSubmit: async (values, { setSubmitting }) => {
      if (!paciente || !horarioSelecionado) return

      setMensagemConflito(undefined)
      setFalhaReserva([])
      setSubmitting(true)
      try {
        // Os eixos da reserva são EXATAMENTE os da consulta: tipo, especialidade,
        // unidade e profissional compõem a chave da vaga. Reservar num eixo e
        // consultar em outro mostra uma ocupação que a fechadura não reconhece.
        const dto: AgendamentoDtoCreate = {
          paciente: paciente.id,
          tipo: values.tipoAtendimento!.value,
          modalidade: values.modalidade!.value,
          inicio: horarioSelecionado.inicio,
          especialidade: values.especialidade?.value ?? null,
          unidade: values.unidade?.value ?? null,
          profissional: profissionalId,
          observacao: values.observacao?.trim() || null,
        }

        const reserva = await criaAgendamento(dto)

        showAlert({
          title: 'Reserva confirmada',
          text: [
            `${formataDiaPorExtenso(dataDoInicio(horarioSelecionado.inicio))}, às ${horaDoInicio(
              horarioSelecionado.inicio
            )} — ${rotuloProfissional(horarioSelecionado)}.`,
            reserva.protocolo ? `Protocolo do atendimento: ${reserva.protocolo}.` : '',
            // `excedente` é decisão do servidor: ele aceitou acima da capacidade.
            reserva.excedente ? 'Registrada acima da capacidade da vaga.' : '',
          ]
            .filter(Boolean)
            .join(' '),
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        if (ehConflitoDeAgenda(error)) {
          // 409: mensagem de negócio, do servidor, LITERAL. Sem enriquecer e sem
          // dizer quem ocupou — o índice nominal atravessa tenants.
          setMensagemConflito(extrairErrosValidacao(error).mensagem ?? MENSAGEM_CONFLITO_PADRAO)
          setHorarioSelecionado(undefined)
          await refetch()
        } else {
          setFalhaReserva(descreveFalhaAgenda(error))
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const tipo = formik.values.tipoAtendimento?.value
  const especialidadeId = formik.values.especialidade?.value ?? null
  const unidadeId = formik.values.unidade?.value ?? null
  const profissionalId =
    formik.values.profissional && formik.values.profissional.value !== VALOR_POOL
      ? Number(formik.values.profissional.value)
      : null

  const especialidadeObrigatoria = exigeEspecialidade(formik.values.tipoAtendimento)
  const filtrosCompletos = Boolean(
    empresaId &&
      tipo &&
      formik.values.inicio &&
      formik.values.fim &&
      formik.values.fim >= formik.values.inicio &&
      (!especialidadeObrigatoria || especialidadeId)
  )

  const {
    data: dias,
    isFetching,
    isError: falhouDisponibilidade,
    error: erroDisponibilidade,
    refetch,
  } = useQuery(
    [
      QUERIES.AGENDA_DISPONIBILIDADE,
      empresaId,
      tipo,
      especialidadeId,
      unidadeId,
      profissionalId,
      formik.values.inicio,
      formik.values.fim,
    ],
    () =>
      getDisponibilidade({
        empresa: empresaId!,
        tipo: tipo!,
        inicio: formik.values.inicio,
        fim: formik.values.fim,
        especialidade: especialidadeId,
        unidade: unidadeId,
        profissional: profissionalId,
      }),
    {
      enabled: filtrosCompletos,
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  // Filtro mudou = a grade anterior não vale mais para a chave nova.
  useEffect(() => {
    setHorarioSelecionado(undefined)
  }, [tipo, especialidadeId, unidadeId, profissionalId, formik.values.inicio, formik.values.fim])

  /**
   * Opções de profissional. A primeira é a **vaga de equipe**, explícita.
   *
   * `getOptionsProfissionalSaude` consulta `GET /funcionarios/medicos`, que
   * devolve `Page<PessoaList>` — o `id` já é o da **Pessoa**, que é o que
   * `AgendamentoDtoCreate.profissional` espera
   * (`@Exists(repository = "pessoaRepository")`). Não é id de `Funcionario`,
   * então o mapeamento de `getOptionsSolicitante` não é necessário aqui.
   */
  const fetchOptionsProfissionais = useMemo(
    () =>
      async (nome: string, selectAll: boolean, extraParams: Object = {}, page = 0) => {
        const { options, hasMore } = await OptionsHelper.getOptionsProfissionalSaude(
          nome,
          selectAll,
          extraParams,
          page
        )
        const comPool =
          page === 0 && !nome
            ? [{ value: VALOR_POOL, label: ROTULO_VAGA_POOL }, ...options]
            : options
        return { options: comPool as IOption[], hasMore }
      },
    []
  )

  const extraParamsEspecialidade = useMemo(
    () => ({ pacienteId: paciente?.id, tipo, unidadeId: unidadeId ?? undefined }),
    [paciente?.id, tipo, unidadeId]
  )

  const mensagemVazio = especialidadeObrigatoria
    ? 'Não há oferta de escala para esta especialidade no período. Troque a data, a especialidade ou o profissional.'
    : 'Não há oferta de escala no período. Troque a data ou o profissional.'

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {paciente && (
        <p className='text-gray-700 mb-4'>
          Agendando para <strong className='text-gray-900'>{paciente.nome}</strong> —{' '}
          {paciente.empresa?.nomeFantasia ?? paciente.empresa?.nome}. A reserva pertence à empresa
          do paciente.
        </p>
      )}

      {mensagemConflito && (
        <div ref={avisoConflitoRef} tabIndex={-1}>
          <AvisoAgenda tom='atencao' titulo='Horário não reservado'>
            {/* Texto do servidor, literal: ele já nasce genérico de propósito. Só
                o próximo passo é acrescentado — nunca quem ocupou o horário. */}
            {mensagemConflito} {MENSAGEM_PROXIMO_PASSO_CONFLITO}
          </AvisoAgenda>
        </div>
      )}

      <AvisoAgenda tom='erro' titulo='Não foi possível concluir:' itens={falhaReserva} />

      <div className='row'>
        {optionsUnidades.length > 0 && (
          <FormSelect
            formik={formik}
            name='unidade'
            label='Unidade/Setor'
            options={optionsUnidades}
            placeholder='Todas as unidades'
            className='col-md-6 mb-5'
            infoText='A unidade compõe a chave de ocupação da vaga, mas não filtra a oferta da escala — é o comportamento do backend, não falha da tela.'
            isDisabled={formik.isSubmitting}
          />
        )}
        <FormSelect
          formik={formik}
          name='tipoAtendimento'
          label='Tipo de atendimento'
          options={optionsTiposAtendimento}
          placeholder='Selecione o tipo'
          className='col-md-6 mb-5'
          isRequired
          isClearable={false}
          isDisabled={formik.isSubmitting}
          onChange={() => formik.setFieldValue('especialidade', null)}
        />
      </div>

      <div className='row'>
        {especialidadeObrigatoria && (
          <FormSelect
            formik={formik}
            name='especialidade'
            label='Especialidade'
            fetchOptions={OptionsHelper.getOptionsEspecialidadesByPaciente}
            fetchOptionsParams={extraParamsEspecialidade}
            placeholder='Selecione a especialidade'
            className='col-md-6 mb-5'
            isRequired
            isClearable={false}
            isDisabled={formik.isSubmitting}
          />
        )}
        <FormSelect
          formik={formik}
          name='modalidade'
          label='Modalidade'
          options={optionsModalidades}
          placeholder='Selecione a modalidade'
          className='col-md-6 mb-5'
          isRequired
          isClearable={false}
          isDisabled={formik.isSubmitting}
        />
      </div>

      <div className='row'>
        <FormSelect
          formik={formik}
          name='profissional'
          label='Profissional'
          fetchOptions={fetchOptionsProfissionais}
          placeholder={ROTULO_VAGA_POOL}
          className='col-md-6 mb-5'
          isClearable={false}
          isDisabled={formik.isSubmitting}
          infoText='Sem profissional escolhido, a grade mostra a vaga da equipe (pool). Escolhendo um profissional, ela passa a mostrar só os horários dele.'
        />
        <FormDateInput
          formik={formik}
          name='inicio'
          label='De'
          className='col-md-3 mb-5'
          isRequired
          isDisabled={formik.isSubmitting}
        />
        <FormDateInput
          formik={formik}
          name='fim'
          label='Até'
          className='col-md-3 mb-5'
          minDate={formik.values.inicio}
          isRequired
          isDisabled={formik.isSubmitting}
        />
      </div>

      <h3 className='fs-5 mb-1'>Horários</h3>
      <p className='text-gray-700 fs-7'>
        {profissionalId
          ? `Vaga com ${formik.values.profissional?.label}.`
          : `Vaga da equipe — ${ROTULO_VAGA_POOL}.`}{' '}
        Pool e profissional são vagas distintas: as contagens não se somam.
      </p>

      {!filtrosCompletos ? (
        <AvisoAgenda tom='neutro' titulo='Escolha os filtros da vaga'>
          Selecione o tipo de atendimento
          {especialidadeObrigatoria ? ', a especialidade' : ''} e o período para ver os horários.
        </AvisoAgenda>
      ) : falhouDisponibilidade && !isFetching ? (
        /* Falha não é "sem oferta": com `keepPreviousData` a grade anterior
           (de outro filtro) continuaria na tela, e sem dados cairia no estado
           vazio — os dois levariam o operador a dizer ao paciente que não há vaga. */
        <>
          <AvisoAgenda
            tom='erro'
            titulo='Não foi possível carregar os horários'
            itens={descreveFalhaAgenda(erroDisponibilidade)}
          />
          <Button className='btn-secondary btn-sm' onClick={() => refetch()}>
            Tentar de novo
          </Button>
        </>
      ) : (
        <AgendaGradeHorarios
          dias={falhouDisponibilidade ? [] : dias ?? []}
          carregando={isFetching}
          inicioSelecionado={horarioSelecionado?.inicio}
          onSelecionar={setHorarioSelecionado}
          mensagemVazio={mensagemVazio}
        />
      )}

      {horarioSelecionado && (
        <div className='border rounded p-4 mt-5' aria-live='polite'>
          <h3 className='fs-6 mb-2'>Horário escolhido</h3>
          <p className='mb-1 text-gray-900'>
            {formataDiaPorExtenso(dataDoInicio(horarioSelecionado.inicio))}, às{' '}
            {horaDoInicio(horarioSelecionado.inicio)}
            {horarioSelecionado.duracaoMinutos
              ? ` · ${horarioSelecionado.duracaoMinutos} minutos`
              : ''}
          </p>
          <p className='mb-1 text-gray-700'>{rotuloProfissional(horarioSelecionado)}</p>
          <p className='mb-3 text-gray-700'>
            Contagem desta vaga: {descreveOcupacao(horarioSelecionado)}
          </p>
          <FormInput
            formik={formik}
            name='observacao'
            label='Observação'
            placeholder='Opcional — até 255 caracteres'
            isDisabled={formik.isSubmitting}
          />
        </div>
      )}

      <div className='d-flex justify-content-end pt-8'>
        <Button
          className='btn-secondary me-3'
          type={ButtonType.Reset}
          onClick={closeModal}
          disabled={formik.isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          className='btn-primary'
          type={ButtonType.Submit}
          disabled={formik.isSubmitting || !horarioSelecionado}
        >
          {formik.isSubmitting ? <LoadingIndicator /> : 'Confirmar reserva'}
        </Button>
      </div>

      {carregandoOpcoes && <ListLoading />}
    </form>
  )
}
